import React, { useRef, type PropsWithChildren } from 'react';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import {
	useHasNudgeActive,
	useNudgeStep,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { useTrackMountedNudgesController } from '@atlassian/jira-software-onboarding-quickstart-track-mounted-nudges/src/index.tsx';
import type {
	ParamsOfEntryPoint,
	AnyEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/jira-entry-point/src/common/types.tsx';
import { ERROR_BOUNDARY_ID_JWM_LIST_TOUR_NUDGE } from '../../../common/constants.tsx';

type AsyncNudgeTargetOptions<TEntryPoint extends AnyEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams: ParamsOfEntryPoint<TEntryPoint>;
	nudgeId: string;
};

export const withAsyncNudgeTarget = <TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	entryPointParams,
	nudgeId,
}: AsyncNudgeTargetOptions<TEntryPoint>) => {
	return function AsyncNudgeTargetWithAsyncNudgeTarget({
		children,
		runtimeProps,
	}: PropsWithChildren<{
		runtimeProps: RuntimePropsOfEntryPoint<TEntryPoint>;
	}>): React.JSX.Element {
		// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			entryPoint,
			entryPointParams,
		);

		useTrackMountedNudgesController(nudgeId);
		const [nudgeStep] = useNudgeStep();
		const [hasNudgeActive] = useHasNudgeActive();
		const entryPointLoaded = useRef(false);

		if (!entryPointLoaded.current && hasNudgeActive && nudgeStep === nudgeId) {
			entryPointActions.load();
			entryPointLoaded.current = true;
		}

		return (
			<>
				{children}
				<JiraEntryPointContainer
					id={ERROR_BOUNDARY_ID_JWM_LIST_TOUR_NUDGE}
					packageName="jiraListOnboarding"
					entryPointReferenceSubject={entryPointReferenceSubject}
					fallback={<></>}
					runtimeProps={runtimeProps}
				/>
			</>
		);
	};
};
